/**
 * Centralized hook for the current user's reputation data.
 * Single React Query source — replaces ad-hoc fetches in Dashboard, Profile, etc.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { UserReputation } from '@/types/reputation';

export const CURRENT_USER_REPUTATION_KEY = 'current-user-reputation';

export async function fetchCurrentUserReputation(userId: string): Promise<UserReputation | null> {
  // Fetch reputation view + boost fields from profiles in parallel
  const [repResult, profileResult] = await Promise.all([
    supabase
      .from('user_reputation')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle(),
    supabase
      .from('profiles')
      .select('reputation_score, boost_plan_total, boost_performance_total')
      .eq('user_id', userId)
      .single(),
  ]);

  if (repResult.error || !repResult.data) return null;

  return {
    ...repResult.data,
    reputation_score: profileResult.data?.reputation_score || 0,
    boost_plan_total: (profileResult.data as any)?.boost_plan_total || 0,
    boost_performance_total: (profileResult.data as any)?.boost_performance_total || 0,
  } as UserReputation;
}

export function useCurrentUserReputation(userId: string | undefined) {
  return useQuery({
    queryKey: [CURRENT_USER_REPUTATION_KEY, userId],
    queryFn: () => fetchCurrentUserReputation(userId!),
    enabled: !!userId,
    staleTime: 5 * 60_000,
    gcTime: 15 * 60_000,
  });
}
